import { Link } from 'react-router-dom';
import type { CartItem } from '@/pages/home/page';
import { useTheme } from '@/hooks/useTheme';

interface Product {
  id: number | string;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  badge: string;
}

interface TrendingProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  thumbnail: string;
  images: string[];
  sizes: string[];
}

interface BannerProps {
  onShopNowClick?: () => void;
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  heroBanners: Product[];
  trendingProducts: TrendingProduct[];
  activeBanner: number;
  goToBanner: (idx: number) => void;
  countdown: { hours: number; minutes: number; seconds: number };
  onDeliveryClick: () => void;
  onSizeClick: () => void;
}

// ── STYLE 1: CLASSIC CAROUSEL (Big + Rounded + Soft Shadow) ──
export function CarouselBanner({
  onShopNowClick, heroBanners, trendingProducts, activeBanner, goToBanner,
  onDeliveryClick, onSizeClick,
}: BannerProps) {
  const { colors } = useTheme();
  const totalBanners = 4 + trendingProducts.length;

  // Get current banner content based on activeBanner
  const renderBannerContent = () => {
    // Banner 0: Delivery
    if (activeBanner === 0) {
      return (
        <div
          onClick={onDeliveryClick}
          className="absolute inset-0 transition-all duration-500 ease-out cursor-pointer"
        >
          <div className="h-full overflow-hidden relative" style={{ borderRadius: 28, background: 'linear-gradient(135deg, #c2410c 0%, #dc2626 50%, #9f1239 100%)' }}>
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #fbbf24, transparent 70%)' }}></div>
            <div className="absolute right-0 top-0 bottom-0 w-28 flex items-center justify-center opacity-10">
              <i className="ri-time-line text-white" style={{ fontSize: 110 }}></i>
            </div>
            <div className="relative z-10 p-5 h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black" style={{ background: 'rgba(253,224,71,0.95)', color: '#7c2d12' }}>
                    <i className="ri-flashlight-fill text-orange-600 text-[10px]"></i>Live Now
                  </span>
                  <span className="text-white/50 text-[10px] font-medium">Tap to read more →</span>
                </div>
                <h2 className="text-white font-black text-2xl leading-tight mb-1.5">
                  Dehradun Delivery <span className="text-yellow-300">30 Min</span>
                </h2>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['ri-map-pin-2-fill|10km Radius|text-rose-200', 'ri-e-bike-2-fill|Live Tracking|text-yellow-200', 'ri-shield-check-fill|COD Available|text-green-300'].map(item => {
                  const [icon, text, color] = item.split('|');
                  return (
                    <div key={text} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)' }}>
                      <i className={`${icon} ${color} text-[10px]`}></i><span>{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Banner 1: Size
    if (activeBanner === 1) {
      return (
        <div
          onClick={onSizeClick}
          className="absolute inset-0 transition-all duration-500 ease-out cursor-pointer"
        >
          <div className="h-full overflow-hidden relative" style={{ borderRadius: 28, background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #0f766e 100%)' }}>
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }}></div>
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #6ee7b7, transparent 70%)' }}></div>
            <div className="absolute right-0 top-0 bottom-0 w-28 flex items-center justify-center opacity-10">
              <i className="ri-t-shirt-line text-white" style={{ fontSize: 110 }}></i>
            </div>
            <div className="relative z-10 p-5 h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black" style={{ background: 'rgba(167,243,208,0.95)', color: '#065f46' }}>
                    <i className="ri-ruler-line text-emerald-700 text-[10px]"></i>Size Guarantee
                  </span>
                  <span className="text-white/50 text-[10px] font-medium">Tap to read more →</span>
                </div>
                <h2 className="text-white font-black text-2xl leading-tight mb-1.5">
                  Wrong Size? <span className="text-emerald-300">No Problem!</span>
                </h2>
                <p className="text-white/75 text-xs">Delivery partner brings S, M, L, XL, XXL — try at door!</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['ri-exchange-line|Try All Sizes|text-emerald-200', 'ri-home-heart-line|At Your Door|text-teal-200', 'ri-check-double-line|Zero Extra Cost|text-green-200'].map(item => {
                  const [icon, text, color] = item.split('|');
                  return (
                    <div key={text} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.15)' }}>
                      <i className={`${icon} ${color} text-[10px]`}></i><span>{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Banner 2: New Arrivals
    if (activeBanner === 2) {
      return (
        <div className="absolute inset-0 transition-all duration-500 ease-out">
          <Link to="/category?cat=T-Shirts" className="block h-full">
            <div className="h-full overflow-hidden relative" style={{ borderRadius: 28 }}>
              <img src="https://readdy.ai/api/search-image?query=Stylish%20young%20man%20wearing%20fresh%20new%20arrival%20streetwear%20collection%2C%20oversized%20hoodie%20and%20track%20pants%2C%20urban%20fashion%20editorial%20photography%2C%20moody%20dark%20studio%20background%20with%20dramatic%20side%20lighting%2C%20high-end%20fashion%20campaign%20aesthetic%2C%20bold%20confident%20pose&width=700&height=320&seq=new_arrivals_banner_01&orientation=landscape" alt="New Arrivals" className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.15) 100%)' }}></div>
              <div className="relative z-10 p-5 h-full flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black" style={{ background: 'rgba(167,243,208,0.95)', color: '#065f46' }}>
                      <i className="ri-sparkling-2-fill text-emerald-600 text-[10px]"></i>Just Dropped
                    </span>
                  </div>
                  <h2 className="text-white font-black text-2xl leading-tight mb-1">New <span className="text-emerald-300">Arrivals</span></h2>
                  <p className="text-white/70 text-xs">Fresh styles, just in — T-Shirts, Hoodies &amp; more</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {['T-Shirts', 'Hoodies', 'Track Pants', 'Jeans'].map(cat => (
                    <span key={cat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>{cat}</span>
                  ))}
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black text-emerald-900 whitespace-nowrap" style={{ background: 'rgba(167,243,208,0.9)' }}>Shop Now →</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      );
    }

    // Banner 3: Sale
    if (activeBanner === 3) {
      return (
        <div className="absolute inset-0 transition-all duration-500 ease-out">
          <Link to="/category?cat=Jeans" className="block h-full">
            <div className="h-full overflow-hidden relative" style={{ borderRadius: 28 }}>
              <img src="https://readdy.ai/api/search-image?query=Fashion%20sale%20promotion%20flat%20lay%20clothing%20arrangement%2C%20multiple%20colorful%20garments%20jeans%20hoodies%20t-shirts%20neatly%20spread%20on%20dark%20surface%2C%20sale%20tags%20visible%2C%20bold%20promotional%20fashion%20photography%2C%20high%20contrast%20dramatic%20lighting%2C%20luxury%20streetwear%20sale%20campaign&width=700&height=320&seq=sale_banner_02&orientation=landscape" alt="Sale" className="absolute inset-0 w-full h-full object-cover object-center" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(120,0,0,0.88) 0%, rgba(180,20,20,0.65) 50%, rgba(0,0,0,0.2) 100%)' }}></div>
              <div className="absolute top-3 right-3 z-20">
                <div className="w-16 h-16 rounded-full flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', boxShadow: '0 4px 20px rgba(251,191,36,0.5)' }}>
                  <span className="text-gray-900 font-black text-xl leading-none">40%</span>
                  <span className="text-gray-900 font-black text-[9px] leading-none">OFF</span>
                </div>
              </div>
              <div className="relative z-10 p-5 h-full flex flex-col justify-between pr-20">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black animate-pulse" style={{ background: 'rgba(253,224,71,0.95)', color: '#7c2d12' }}>
                      <i className="ri-price-tag-3-fill text-red-600 text-[10px]"></i>Limited Time
                    </span>
                  </div>
                  <h2 className="text-white font-black text-2xl leading-tight mb-1">Big <span className="text-yellow-300">Sale</span> On!</h2>
                  <p className="text-white/70 text-xs">Upto 40% off on Jeans, Shorts &amp; more</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {['Jeans', 'Shorts', 'Hoodies'].map(cat => (
                    <span key={cat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.2)' }}>{cat}</span>
                  ))}
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black text-red-900 whitespace-nowrap" style={{ background: 'rgba(253,224,71,0.92)' }}>Grab Now →</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      );
    }

    // Trending Product Banners (4+)
    const tpIdx = activeBanner - 4;
    if (tpIdx >= 0 && tpIdx < trendingProducts.length) {
      const tp = trendingProducts[tpIdx];
      const hasImage = tp.thumbnail || (tp.images && tp.images.length > 0);
      return (
        <div className="absolute inset-0 transition-all duration-500 ease-out">
          <Link to={`/category?product=${encodeURIComponent(tp.id)}`} className="block h-full">
            <div className="h-full overflow-hidden relative" style={{ borderRadius: 28, background: 'linear-gradient(135deg, #1c1917 0%, #292524 50%, #44403c 100%)' }}>
              {hasImage && (
                <div className="absolute right-0 top-0 bottom-0 w-36 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917] via-[#1c1917]/60 to-transparent z-10"></div>
                  <img src={tp.thumbnail || tp.images?.[0]} alt={tp.name} className="w-full h-full object-cover object-center" />
                </div>
              )}
              <div className={`relative z-10 p-5 h-full flex flex-col justify-between ${hasImage ? 'pr-36' : ''}`}>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black" style={{ background: 'rgba(253,224,71,0.95)', color: '#78350f' }}>
                      <i className="ri-fire-fill text-orange-600 text-[10px]"></i>Trending
                    </span>
                  </div>
                  <h2 className="text-white font-black text-xl leading-tight mb-1.5 line-clamp-1">{tp.name}</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-300 font-black text-xl">₹{tp.price}</span>
                    {tp.originalPrice > tp.price && <span className="text-white/40 text-xs line-through">₹{tp.originalPrice}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {tp.sizes.slice(0, 4).map(s => (
                    <span key={s} className="px-2 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>{s}</span>
                  ))}
                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-yellow-300 whitespace-nowrap" style={{ background: 'rgba(0,0,0,0.4)' }}>Shop Now →</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        borderRadius: 28,
        minHeight: 200,
        boxShadow: `0 20px 60px -10px ${colors.primary}40, 0 8px 24px rgba(0,0,0,0.18)`,
      }}
    >
      {renderBannerContent()}

      {/* Spacer for height */}
      <div className="invisible relative p-5" aria-hidden="true">
        <div className="mb-2 h-6"></div>
        <div className="text-2xl font-black mb-2">placeholder</div>
        <div className="flex gap-1.5"><div className="h-5 w-20"></div></div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
        {Array.from({ length: totalBanners }).map((_, idx) => (
          <button key={idx} onClick={() => goToBanner(idx)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${activeBanner === idx ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/60'}`}
          />
        ))}
      </div>
    </div>
  );
}

// ── STYLE 2: 3D CARD STACK ──
export function Card3DBanner({ onShopNowClick, heroBanners }: BannerProps) {
  const { colors } = useTheme();
  const items = heroBanners.slice(0, 3);

  return (
    <div className="relative" style={{ minHeight: 280 }}>
      {/* Background glow */}
      <div className="absolute inset-0 rounded-3xl opacity-30 blur-3xl" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}></div>

      {/* Stacked cards */}
      <div className="relative flex items-center justify-center" style={{ minHeight: 280 }}>
        {/* Card 3 (back) */}
        <div className="absolute w-[88%] rounded-3xl overflow-hidden" style={{
          height: 220,
          top: 24,
          transform: 'rotate(4deg) scale(0.92)',
          background: `linear-gradient(135deg, ${colors.secondary}cc, ${colors.primary}cc)`,
          boxShadow: `0 30px 60px -10px ${colors.primary}50`,
          zIndex: 1,
        }}>
          {items[2] && <img src={items[2].image} alt="" className="w-full h-full object-cover object-top opacity-60" />}
        </div>

        {/* Card 2 (middle) */}
        <div className="absolute w-[92%] rounded-3xl overflow-hidden" style={{
          height: 240,
          top: 12,
          transform: 'rotate(-2deg) scale(0.96)',
          background: `linear-gradient(135deg, ${colors.primary}dd, ${colors.secondary}dd)`,
          boxShadow: `0 30px 60px -10px ${colors.primary}60`,
          zIndex: 2,
        }}>
          {items[1] && <img src={items[1].image} alt="" className="w-full h-full object-cover object-top opacity-70" />}
        </div>

        {/* Card 1 (front — main) */}
        <div className="relative w-full rounded-3xl overflow-hidden" style={{
          height: 260,
          zIndex: 3,
          boxShadow: `0 40px 80px -15px ${colors.primary}70, 0 0 0 1px ${colors.primary}30`,
          transform: 'perspective(1000px) rotateX(2deg)',
        }}>
          {items[0] && <img src={items[0].image} alt={items[0]?.name} className="absolute inset-0 w-full h-full object-cover object-top" />}
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${colors.primary}cc 0%, rgba(0,0,0,0.5) 60%, transparent 100%)` }}></div>

          {/* Inner glow border */}
          <div className="absolute inset-0 rounded-3xl" style={{ boxShadow: `inset 0 0 40px ${colors.primary}40, inset 0 1px 0 rgba(255,255,255,0.2)` }}></div>

          <div className="relative z-10 p-6 h-full flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black mb-3" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff' }}>
                <i className="ri-vip-crown-fill" style={{ color: colors.accent }}></i>Featured Pick
              </span>
              <h2 className="text-white font-black text-2xl md:text-3xl leading-tight mb-1">{items[0]?.name}</h2>
              <div className="flex items-center gap-2 mt-2">
                <span className="font-black text-2xl" style={{ color: colors.accent }}>₹{items[0]?.price}</span>
                <span className="text-white/50 text-sm line-through">₹{items[0]?.originalPrice}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-black text-white" style={{ background: colors.primary }}>
                  {items[0] ? Math.round((1 - items[0].price / items[0].originalPrice) * 100) : 0}% OFF
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={onShopNowClick} className="px-5 py-2.5 rounded-xl font-bold text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 8px 24px ${colors.primary}60` }}>
                <i className="ri-shopping-bag-3-line"></i>Shop Now
              </button>
              <Link to="/category" className="px-5 py-2.5 rounded-xl font-bold text-sm text-white flex items-center gap-2 whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <i className="ri-eye-line"></i>View All
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STYLE 3: GLASSMORPHISM HERO ──
export function GlassBanner({ onShopNowClick }: BannerProps) {
  const { colors } = useTheme();

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 300 }}>
      {/* Background image */}
      <img
        src="https://readdy.ai/api/search-image?query=Dramatic%20fashion%20editorial%20photography%2C%20young%20Indian%20male%20model%20wearing%20premium%20oversized%20streetwear%2C%20dark%20moody%20cinematic%20background%20with%20deep%20shadows%20and%20dramatic%20lighting%2C%20luxury%20fashion%20campaign%2C%20full%20body%20confident%20pose%2C%20urban%20aesthetic%2C%20professional%20studio%20photography&width=900&height=400&seq=glass_hero_bg&orientation=landscape"
        alt="Fashion"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.1) 100%)' }}></div>

      {/* Floating glass cards */}
      <div className="relative z-10 p-5 md:p-8 flex flex-col md:flex-row items-center gap-5 min-h-[300px]">
        {/* Main content */}
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.accent }}></div>
            <span className="text-white text-xs font-bold">New Collection 2025</span>
          </div>
          <h2 className="text-white font-black text-3xl md:text-4xl leading-tight mb-3">
            Premium<br /><span style={{ color: colors.accent }}>Streetwear</span>
          </h2>
          <p className="text-white/70 text-sm mb-5 max-w-xs">Dehradun&apos;s finest fashion. Delivered in 30 minutes.</p>
          <button onClick={onShopNowClick} className="px-6 py-3 rounded-2xl font-bold text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105 w-fit" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 12px 32px ${colors.primary}60` }}>
            <i className="ri-shopping-bag-3-line"></i>Shop Now
          </button>
        </div>

        {/* Glass stats panel */}
        <div className="grid grid-cols-2 gap-3 w-full md:w-auto md:min-w-[200px]">
          {[
            { icon: 'ri-flashlight-fill', label: '30 Min', sub: 'Delivery', color: colors.accent },
            { icon: 'ri-star-fill', label: '4.8★', sub: 'Rating', color: '#fbbf24' },
            { icon: 'ri-t-shirt-line', label: '500+', sub: 'Products', color: colors.primary },
            { icon: 'ri-shield-check-fill', label: 'COD', sub: 'Available', color: '#34d399' },
          ].map(stat => (
            <div key={stat.label} className="p-3 rounded-2xl text-center" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
              <i className={`${stat.icon} text-xl mb-1`} style={{ color: stat.color }}></i>
              <p className="text-white font-black text-sm">{stat.label}</p>
              <p className="text-white/60 text-[10px]">{stat.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom glass bar */}
      <div className="absolute bottom-0 left-0 right-0 px-5 py-3 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        {['Track Pants', 'T-Shirts', 'Jeans', 'Hoodies', 'Shorts'].map(cat => (
          <Link key={cat} to={`/category?cat=${encodeURIComponent(cat)}`} className="text-white/80 text-xs font-semibold whitespace-nowrap hover:text-white transition-colors">{cat}</Link>
        ))}
      </div>
    </div>
  );
}

// ── STYLE 4: NEON GLOW DARK ──
export function NeonGlowBanner({ onShopNowClick, countdown }: BannerProps) {
  const { colors } = useTheme();

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 300, background: '#0a0a0a', boxShadow: `0 0 60px ${colors.primary}40, 0 0 120px ${colors.primary}20` }}>
      {/* Neon grid lines */}
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `linear-gradient(${colors.primary}40 1px, transparent 1px), linear-gradient(90deg, ${colors.primary}40 1px, transparent 1px)`, backgroundSize: '40px 40px' }}></div>

      {/* Neon glow orbs */}
      <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: colors.primary }}></div>
      <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full opacity-15 blur-3xl" style={{ background: colors.secondary }}></div>

      <div className="relative z-10 p-6 md:p-10 flex flex-col items-center justify-center text-center min-h-[300px]">
        {/* Neon badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ border: `1px solid ${colors.primary}80`, boxShadow: `0 0 20px ${colors.primary}40, inset 0 0 20px ${colors.primary}10` }}>
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.primary, boxShadow: `0 0 8px ${colors.primary}` }}></div>
          <span className="text-xs font-bold" style={{ color: colors.primary }}>FLASH SALE LIVE</span>
        </div>

        <h2 className="font-black text-3xl md:text-5xl leading-tight mb-2" style={{ color: '#fff', textShadow: `0 0 30px ${colors.primary}80, 0 0 60px ${colors.primary}40` }}>
          UP TO <span style={{ color: colors.accent, textShadow: `0 0 30px ${colors.accent}` }}>50% OFF</span>
        </h2>
        <p className="text-white/50 text-sm mb-5">Limited time. Limited stock. Don&apos;t miss out.</p>

        {/* Countdown */}
        <div className="flex items-center gap-3 mb-6">
          {[
            { value: countdown.hours, label: 'HRS' },
            { value: countdown.minutes, label: 'MIN' },
            { value: countdown.seconds, label: 'SEC' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${colors.primary}60`, boxShadow: `0 0 20px ${colors.primary}30, inset 0 0 20px ${colors.primary}10` }}>
                <span className="font-black text-xl" style={{ color: colors.primary, textShadow: `0 0 10px ${colors.primary}` }}>
                  {String(item.value).padStart(2, '0')}
                </span>
              </div>
              <span className="text-[10px] font-bold mt-1" style={{ color: colors.primary + '80' }}>{item.label}</span>
            </div>
          ))}
        </div>

        <button onClick={onShopNowClick} className="px-8 py-3.5 rounded-2xl font-black text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, color: '#fff', boxShadow: `0 0 30px ${colors.primary}60, 0 8px 24px ${colors.primary}40` }}>
          <i className="ri-flashlight-fill mr-2"></i>Shop Sale Now
        </button>
      </div>
    </div>
  );
}

// ── STYLE 5: BIG SPLIT 3D ──
export function BigSplit3DBanner({ onShopNowClick, heroBanners }: BannerProps) {
  const { colors } = useTheme();
  const featured = heroBanners[0];

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 320, background: `linear-gradient(135deg, #0f0f0f, #1a1a1a)`, boxShadow: `0 40px 80px -20px ${colors.primary}50, 0 0 0 1px rgba(255,255,255,0.05)` }}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }}></div>

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 min-h-[320px]">
        {/* Left: Product 3D card */}
        <div className="relative flex items-center justify-center p-6 md:p-8">
          {/* 3D platform */}
          <div className="relative">
            {/* Shadow/platform */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-40 h-8 rounded-full blur-xl opacity-60" style={{ background: colors.primary }}></div>
            {/* Glow ring */}
            <div className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}></div>
            {/* Product image */}
            <div className="relative w-44 md:w-52" style={{ transform: 'perspective(800px) rotateY(-8deg) rotateX(4deg)', filter: 'drop-shadow(0 30px 40px rgba(0,0,0,0.8))' }}>
              {featured ? (
                <img src={featured.image} alt={featured.name} className="w-full aspect-[3/4] object-cover object-top rounded-2xl" />
              ) : (
                <div className="w-full aspect-[3/4] rounded-2xl" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}></div>
              )}
              {/* Badge */}
              <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full flex flex-col items-center justify-center" style={{ background: `linear-gradient(135deg, ${colors.accent}, ${colors.primary})`, boxShadow: `0 8px 24px ${colors.primary}60` }}>
                <span className="text-white font-black text-xs leading-none">{featured ? Math.round((1 - featured.price / featured.originalPrice) * 100) : 40}%</span>
                <span className="text-white font-black text-[8px] leading-none">OFF</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Content */}
        <div className="flex flex-col justify-center p-6 md:p-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full w-fit mb-4" style={{ background: `${colors.primary}20`, border: `1px solid ${colors.primary}40` }}>
            <i className="ri-sparkling-fill text-xs" style={{ color: colors.accent }}></i>
            <span className="text-xs font-bold" style={{ color: colors.primary }}>Featured Drop</span>
          </div>
          <h2 className="text-white font-black text-2xl md:text-3xl leading-tight mb-2">
            {featured?.name || 'Premium Streetwear'}
          </h2>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-black text-2xl" style={{ color: colors.accent }}>₹{featured?.price || 699}</span>
            <span className="text-white/40 text-sm line-through">₹{featured?.originalPrice || 999}</span>
          </div>
          <p className="text-white/60 text-sm mb-5">Premium quality. Perfect fit. Dehradun&apos;s favorite pick.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={onShopNowClick} className="px-5 py-2.5 rounded-xl font-bold text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 8px 24px ${colors.primary}50` }}>
              <i className="ri-shopping-bag-3-line"></i>Shop Now
            </button>
            <Link to="/category" className="px-5 py-2.5 rounded-xl font-bold text-sm text-white flex items-center gap-2 whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <i className="ri-grid-fill"></i>Browse All
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── STYLE 6: MAGAZINE GRID ──
export function MagazineGridBanner({ onShopNowClick, heroBanners }: BannerProps) {
  const { colors } = useTheme();
  const items = heroBanners.slice(0, 4);

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ boxShadow: `0 30px 60px -15px ${colors.primary}40` }}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1" style={{ minHeight: 300 }}>
        {/* Big left card */}
        <div className="relative overflow-hidden md:col-span-2 row-span-2 group cursor-pointer" style={{ minHeight: 200 }}>
          <Link to="/category" className="block h-full">
            {items[0] ? (
              <img src={items[0].image} alt={items[0].name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" style={{ minHeight: 200 }} />
            ) : (
              <div className="w-full h-full" style={{ minHeight: 200, background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
            {/* Inner theme glow */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(135deg, ${colors.primary}30, transparent)` }}></div>
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-black text-white mb-2" style={{ background: colors.primary }}>BESTSELLER</span>
              <h3 className="text-white font-black text-xl leading-tight mb-1">{items[0]?.name || 'Premium Collection'}</h3>
              <p className="text-white/70 text-sm">From ₹{items[0]?.price || 699}</p>
            </div>
          </Link>
        </div>

        {/* Right small cards */}
        {[items[1], items[2]].map((item, i) => (
          <div key={i} className="relative overflow-hidden group cursor-pointer" style={{ minHeight: 140 }}>
            <Link to="/category" className="block h-full">
              {item ? (
                <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" style={{ minHeight: 140 }} />
              ) : (
                <div className="w-full h-full" style={{ minHeight: 140, background: `linear-gradient(135deg, ${colors.secondary}, ${colors.accent})` }}></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `${colors.primary}20` }}></div>
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-bold text-xs leading-tight">{item?.name || 'Collection'}</p>
                <p className="text-white/70 text-[10px]">₹{item?.price || 699}</p>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Bottom CTA bar */}
      <div className="absolute bottom-0 left-0 right-0 hidden md:flex items-center justify-between px-5 py-3" style={{ background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})` }}>
        <span className="text-white font-bold text-sm">Explore Full Collection</span>
        <button onClick={onShopNowClick} className="px-4 py-1.5 rounded-lg bg-white font-bold text-xs cursor-pointer whitespace-nowrap hover:scale-105 transition-all" style={{ color: colors.primary }}>
          Shop Now →
        </button>
      </div>
    </div>
  );
}

// ── STYLE 7: FULLSCREEN CINEMATIC ──
export function CinematicBanner({ onShopNowClick }: BannerProps) {
  const { colors } = useTheme();

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 380, boxShadow: `0 40px 80px -20px rgba(0,0,0,0.6)` }}>
      <img
        src="https://readdy.ai/api/search-image?query=Cinematic%20fashion%20editorial%20photography%2C%20young%20Indian%20male%20model%20wearing%20premium%20oversized%20black%20hoodie%20and%20baggy%20cargo%20pants%2C%20powerful%20confident%20full%20body%20stance%2C%20dramatic%20cinematic%20lighting%20with%20deep%20shadows%20and%20rim%20light%2C%20luxury%20streetwear%20campaign%2C%20professional%20high-end%20fashion%20editorial%2C%20dark%20moody%20atmosphere%2C%20wide%20angle%20composition&width=900&height=500&seq=cinematic_hero_v2&orientation=landscape"
        alt="Cinematic Fashion"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      {/* Cinematic letterbox bars */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-black z-10"></div>
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-black z-10"></div>
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent"></div>
      {/* Theme color tint */}
      <div className="absolute inset-0 opacity-20" style={{ background: `linear-gradient(135deg, ${colors.primary}60, transparent)` }}></div>

      <div className="relative z-20 flex flex-col justify-center h-full min-h-[380px] p-6 md:p-10">
        {/* Film grain effect text */}
        <p className="text-xs font-bold uppercase tracking-[0.4em] mb-3" style={{ color: colors.accent }}>ClothyBazar Presents</p>
        <h2 className="text-white font-black text-3xl md:text-5xl leading-tight mb-3" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
          Own Your<br />
          <span style={{ color: colors.accent, textShadow: `0 0 30px ${colors.accent}60` }}>Story</span>
        </h2>
        <p className="text-white/70 text-sm md:text-base mb-6 max-w-sm">
          Premium streetwear for Dehradun&apos;s boldest. 30-min delivery. COD available.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={onShopNowClick} className="px-7 py-3.5 rounded-2xl font-black text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 12px 32px ${colors.primary}60` }}>
            <i className="ri-shopping-bag-3-line"></i>Shop Now
          </button>
          <Link to="/category" className="px-7 py-3.5 rounded-2xl font-black text-sm text-white flex items-center gap-2 whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <i className="ri-play-circle-line"></i>Explore
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── STYLE 8: INNER THEME SHOWCASE ──
export function ThemeShowcaseBanner({ onShopNowClick, heroBanners }: BannerProps) {
  const { colors } = useTheme();
  const items = heroBanners.slice(0, 3);

  return (
    <div className="relative overflow-hidden rounded-3xl p-5 md:p-8" style={{ background: `linear-gradient(135deg, ${colors.bg}, ${colors.cardBg || '#f8f8f8'})`, border: `2px solid ${colors.primary}20`, boxShadow: `0 30px 60px -15px ${colors.primary}30, inset 0 1px 0 rgba(255,255,255,0.8)` }}>
      {/* Decorative background shapes */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2" style={{ background: `radial-gradient(circle, ${colors.primary}, transparent)` }}></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10 translate-y-1/2 -translate-x-1/2" style={{ background: `radial-gradient(circle, ${colors.secondary}, transparent)` }}></div>

      <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
        {/* Left: Text */}
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: `${colors.primary}15`, border: `1px solid ${colors.primary}30` }}>
            <div className="w-2 h-2 rounded-full" style={{ background: colors.primary }}></div>
            <span className="text-xs font-bold" style={{ color: colors.primary }}>New Season Drop</span>
          </div>
          <h2 className="font-black text-2xl md:text-3xl leading-tight mb-2" style={{ color: colors.text }}>
            Style That<br />
            <span style={{ color: colors.primary }}>Speaks</span> For You
          </h2>
          <p className="text-sm mb-5" style={{ color: colors.muted }}>
            Premium streetwear. Authentic fits. Dehradun&apos;s #1 fashion store.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={onShopNowClick} className="px-5 py-2.5 rounded-xl font-bold text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 8px 24px ${colors.primary}40` }}>
              <i className="ri-shopping-bag-3-line"></i>Shop Now
            </button>
            <Link to="/category" className="px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all hover:scale-105" style={{ background: `${colors.primary}10`, color: colors.primary, border: `1px solid ${colors.primary}30` }}>
              <i className="ri-grid-fill"></i>Browse
            </Link>
          </div>
        </div>

        {/* Right: Product cards with theme colors */}
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map((item, i) => (
            <Link key={item.id} to="/category" className="shrink-0 w-28 md:w-32 rounded-2xl overflow-hidden group cursor-pointer transition-all hover:scale-105" style={{ boxShadow: `0 12px 32px ${colors.primary}30, 0 0 0 1px ${colors.primary}15` }}>
              <div className="relative aspect-[3/4] overflow-hidden">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `${colors.primary}20` }}></div>
              </div>
              <div className="p-2" style={{ background: i === 0 ? colors.primary : colors.cardBg || '#fff' }}>
                <p className={`font-bold text-[10px] leading-tight truncate ${i === 0 ? 'text-white' : ''}`} style={i !== 0 ? { color: colors.text } : {}}>
                  {item.name}
                </p>
                <p className={`font-black text-xs ${i === 0 ? 'text-white/80' : ''}`} style={i !== 0 ? { color: colors.primary } : {}}>
                  ₹{item.price}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── STYLE 9: ANNOUNCEMENT MARQUEE ──
export function AnnouncementBanner({ onShopNowClick }: BannerProps) {
  const { colors } = useTheme();

  return (
    <div className="space-y-2">
      {/* Top marquee bar */}
      <div className="relative overflow-hidden rounded-2xl py-2.5 px-4 flex items-center gap-4" style={{ background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, ${colors.primary})`, backgroundSize: '200% 100%' }}>
        <i className="ri-megaphone-fill text-white text-lg shrink-0"></i>
        <div className="flex-1 overflow-hidden relative h-5">
          <div className="absolute whitespace-nowrap text-white text-sm font-medium" style={{ animation: 'marquee 20s linear infinite' }}>
            🔥 40% OFF on all Jeans &amp; Shorts &nbsp;&nbsp;|&nbsp;&nbsp; 🚚 Free delivery above ₹999 &nbsp;&nbsp;|&nbsp;&nbsp; 💰 Cash on delivery &nbsp;&nbsp;|&nbsp;&nbsp; 📍 Dehradun 30-min delivery &nbsp;&nbsp;|&nbsp;&nbsp; ✨ New arrivals every week &nbsp;&nbsp;|&nbsp;&nbsp; 🎁 Gift codes available &nbsp;&nbsp;|&nbsp;&nbsp;
          </div>
        </div>
        <button onClick={onShopNowClick} className="shrink-0 px-4 py-1.5 rounded-lg bg-white font-bold text-xs cursor-pointer whitespace-nowrap hover:scale-105 transition-all" style={{ color: colors.primary }}>
          Shop Now
        </button>
      </div>

      {/* Main hero below */}
      <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 260, boxShadow: `0 20px 50px -10px ${colors.primary}40` }}>
        <img
          src="https://readdy.ai/api/search-image?query=Fashion%20sale%20promotion%20banner%2C%20stylish%20young%20Indian%20man%20wearing%20trendy%20streetwear%2C%20bold%20promotional%20fashion%20photography%2C%20high%20contrast%20dramatic%20lighting%2C%20luxury%20streetwear%20sale%20campaign%2C%20wide%20banner%20composition%2C%20dark%20moody%20background&width=900&height=360&seq=announcement_hero_v2&orientation=landscape"
          alt="Sale"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.1) 100%)` }}></div>
        <div className="relative z-10 p-6 md:p-8 flex flex-col justify-center min-h-[260px]">
          <h2 className="text-white font-black text-2xl md:text-3xl leading-tight mb-2">
            Biggest Sale<br /><span style={{ color: colors.accent }}>of the Season!</span>
          </h2>
          <p className="text-white/70 text-sm mb-4">Up to 50% off. Limited time. Shop before it&apos;s gone.</p>
          <button onClick={onShopNowClick} className="px-6 py-3 rounded-2xl font-bold text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105 w-fit" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 8px 24px ${colors.primary}50` }}>
            <i className="ri-flashlight-fill"></i>Grab Deals
          </button>
        </div>
      </div>
    </div>
  );
}

// ── STYLE 10: MINIMAL LUXURY ──
export function MinimalLuxuryBanner({ onShopNowClick, heroBanners }: BannerProps) {
  const { colors } = useTheme();
  const featured = heroBanners[0];

  return (
    <div className="relative overflow-hidden rounded-3xl" style={{ minHeight: 300, background: '#fafafa', border: `1px solid ${colors.primary}15`, boxShadow: `0 30px 60px -15px ${colors.primary}25, 0 0 0 1px ${colors.primary}10` }}>
      {/* Subtle gradient bg */}
      <div className="absolute inset-0 opacity-30" style={{ background: `radial-gradient(ellipse at top right, ${colors.primary}20, transparent 60%), radial-gradient(ellipse at bottom left, ${colors.secondary}15, transparent 60%)` }}></div>

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 min-h-[300px]">
        {/* Left: Typography */}
        <div className="flex flex-col justify-center p-7 md:p-10">
          <p className="text-xs font-bold uppercase tracking-[0.4em] mb-4" style={{ color: colors.secondary }}>Est. 2024 · Dehradun</p>
          <h2 className="font-black text-3xl md:text-4xl leading-tight mb-3" style={{ color: colors.text }}>
            CLOTHY<span style={{ color: colors.primary }}>BAZAR</span>
          </h2>
          <div className="w-12 h-1 rounded-full mb-4" style={{ background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})` }}></div>
          <p className="text-sm mb-6" style={{ color: colors.muted }}>
            Premium streetwear. Authentic style.<br />Lightning fast delivery.
          </p>
          {/* Stats */}
          <div className="flex gap-5 mb-6">
            {[
              { num: '500+', label: 'Products' },
              { num: '30min', label: 'Delivery' },
              { num: '4.8★', label: 'Rating' },
            ].map(s => (
              <div key={s.label}>
                <p className="font-black text-lg" style={{ color: colors.primary }}>{s.num}</p>
                <p className="text-xs" style={{ color: colors.muted }}>{s.label}</p>
              </div>
            ))}
          </div>
          <button onClick={onShopNowClick} className="px-6 py-3 rounded-xl font-bold text-sm text-white flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all hover:scale-105 w-fit" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 8px 24px ${colors.primary}40` }}>
            <i className="ri-arrow-right-line"></i>Start Shopping
          </button>
        </div>

        {/* Right: Product showcase */}
        <div className="relative flex items-center justify-center p-6">
          {/* Decorative circles */}
          <div className="absolute w-48 h-48 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${colors.primary}, transparent)` }}></div>
          <div className="relative">
            {featured ? (
              <div className="relative">
                <div className="absolute -inset-3 rounded-3xl opacity-20 blur-xl" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}></div>
                <img src={featured.image} alt={featured.name} className="relative w-40 md:w-48 aspect-[3/4] object-cover object-top rounded-2xl" style={{ boxShadow: `0 20px 50px -10px ${colors.primary}50` }} />
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full whitespace-nowrap" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, boxShadow: `0 8px 20px ${colors.primary}50` }}>
                  <span className="text-white font-black text-sm">₹{featured.price}</span>
                </div>
              </div>
            ) : (
              <div className="w-40 aspect-[3/4] rounded-2xl" style={{ background: `linear-gradient(135deg, ${colors.primary}30, ${colors.secondary}30)` }}></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}